import { sendGTMEvent } from '@next/third-parties/google';
import { useCallback } from 'react';

// Tipos para los eventos de analytics siguiendo la estructura de GA4
export interface PageViewEvent {
  page_title: string;
  page_location: string;
  page_path?: string;
}

// Estructura de items según GA4 Ecommerce
export interface EcommerceItem {
  item_id: string; // Requerido - ID único del producto
  item_name: string; // Requerido - Nombre del producto
  item_category?: string; // Categoría principal
  item_category2?: string; // Subcategoría
  item_category3?: string; // Sub-subcategoría
  item_category4?: string; // Categoría adicional
  item_category5?: string; // Categoría adicional
  item_brand?: string; // Marca del producto
  item_variant?: string; // Variante del producto (color, tamaño, etc.)
  item_list_id?: string; // ID de la lista donde aparece el producto
  item_list_name?: string; // Nombre de la lista
  affiliation?: string; // Afiliación o tienda
  coupon?: string; // Cupón aplicado
  discount?: number; // Descuento aplicado
  index?: number; // Posición en la lista
  location_id?: string; // ID de la ubicación
  price?: number; // Precio del producto
  quantity?: number; // Cantidad
  currency?: string; // Moneda del precio
  bar_code?: string; // Código de barras del producto
}

export interface ViewItemEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
}

export interface ViewItemListEvent {
  item_list_id: string;
  item_list_name: string;
  items: EcommerceItem[];
}

export interface SelectItemEvent {
  item_list_id: string;
  item_list_name: string;
  items: EcommerceItem[];
}

export interface AddToCartEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
}

export interface RemoveFromCartEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
}

export interface ViewCartEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
}

export interface BeginCheckoutEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
  coupon?: string;
}

export interface AddPersonalInfoEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
  coupon?: string;
}

export interface AddShippingInfoEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
  shipping_tier?: string;
  coupon?: string;
}

export interface AddPaymentInfoEvent {
  currency: string;
  value: number;
  items: EcommerceItem[];
  payment_type?: string;
  coupon?: string;
}

export interface PurchaseEvent {
  transaction_id: string;
  value: number;
  tax: number;
  shipping: number;
  currency: string;
  items: EcommerceItem[];
  coupon?: string;
  affiliation?: string;
  shipping_tier?: string;
}

export interface RefundEvent {
  transaction_id: string;
  value: number;
  tax: number;
  shipping: number;
  currency: string;
  items: EcommerceItem[];
}

export interface ViewPromotionEvent {
  creative_name?: string;
  creative_slot?: string;
  promotion_id?: string;
  promotion_name?: string;
  items: EcommerceItem[];
}

export interface SelectPromotionEvent {
  creative_name?: string;
  creative_slot?: string;
  promotion_id?: string;
  promotion_name?: string;
  items: EcommerceItem[];
}

export interface CustomEvent {
  event: string;
  [key: string]: any;
}

export const useAnalytics = () => {
  // Función auxiliar para limpiar ecommerce antes de enviar un nuevo evento
  const clearEcommerce = useCallback(() => {
    try {
      sendGTMEvent({
        event: 'clear_ecommerce',
        ecommerce: null,
      });
    } catch (error) {
      // Silenciar errores de limpieza
    }
  }, []);

  // Función para enviar eventos de page_view
  const trackPageView = useCallback(
    (data: PageViewEvent) => {
      try {
        clearEcommerce();
        sendGTMEvent({
          event: 'page_view',
          ...data,
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    },
    [clearEcommerce]
  );

  // Función para enviar eventos de view_item
  const trackViewItem = useCallback(
    (data: ViewItemEvent) => {
      try {
        clearEcommerce();
        sendGTMEvent({
          event: 'view_item',
          ecommerce: data,
        });
      } catch (error) {
        console.error('Error tracking view item:', error);
      }
    },
    [clearEcommerce]
  );

  // Función para enviar eventos de view_item_list
  const trackViewItemList = useCallback(
    (data: ViewItemListEvent) => {
      try {
        clearEcommerce();
        sendGTMEvent({
          event: 'view_item_list',
          ecommerce: data,
        });
      } catch (error) {
        console.error('Error tracking view item list:', error);
      }
    },
    [clearEcommerce]
  );

  // Función para enviar eventos de select_item
  const trackSelectItem = useCallback(
    (data: SelectItemEvent) => {
      try {
        clearEcommerce();
        sendGTMEvent({
          event: 'select_item',
          ecommerce: data,
        });
      } catch (error) {
        console.error('Error tracking select item:', error);
      }
    },
    [clearEcommerce]
  );

  // Función para enviar eventos de add_to_cart
  const trackAddToCart = (data: AddToCartEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'add_to_cart',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking add to cart:', error);
    }
  };

  // Función para enviar eventos de remove_from_cart
  const trackRemoveFromCart = (data: RemoveFromCartEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'remove_from_cart',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking remove from cart:', error);
    }
  };

  // Función para enviar eventos de view_cart
  const trackViewCart = (data: ViewCartEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'view_cart',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking view cart:', error);
    }
  };

  // Función para enviar eventos de begin_checkout
  const trackBeginCheckout = (data: BeginCheckoutEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'begin_checkout',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking begin checkout:', error);
    }
  };

  // Función para enviar eventos de add_personal_info
  const trackAddPersonalInfo = (data: AddPersonalInfoEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'add_personal_info',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking add personal info:', error);
    }
  };

  // Función para enviar eventos de add_shipping_info
  const trackAddShippingInfo = (data: AddShippingInfoEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'add_shipping_info',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking add shipping info:', error);
    }
  };

  // Función para enviar eventos de add_payment_info
  const trackAddPaymentInfo = (data: AddPaymentInfoEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'add_payment_info',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking add payment info:', error);
    }
  };

  // Función para enviar eventos de purchase
  const trackPurchase = (data: PurchaseEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'purchase',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking purchase:', error);
    }
  };

  // Función para enviar eventos de refund
  const trackRefund = (data: RefundEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'refund',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking refund:', error);
    }
  };

  // Función para enviar eventos de view_promotion
  const trackViewPromotion = (data: ViewPromotionEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'view_promotion',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking view promotion:', error);
    }
  };

  // Función para enviar eventos de select_promotion
  const trackSelectPromotion = (data: SelectPromotionEvent) => {
    try {
      clearEcommerce();
      sendGTMEvent({
        event: 'select_promotion',
        ecommerce: data,
      });
    } catch (error) {
      console.error('Error tracking select promotion:', error);
    }
  };

  // Función para enviar eventos personalizados
  const trackCustomEvent = (data: CustomEvent) => {
    try {
      sendGTMEvent(data);
    } catch (error) {
      console.error('Error tracking custom event:', error);
    }
  };

  // Función para enviar evento de sign_up
  const trackSignUp = () => {
    try {
      sendGTMEvent({
        event: 'sign_up',
      });
    } catch (error) {
      console.error('Error tracking sign up:', error);
    }
  };

  // Función para enviar evento de login
  const trackLogin = () => {
    try {
      sendGTMEvent({
        event: 'login',
      });
    } catch (error) {
      console.error('Error tracking login:', error);
    }
  };

  // Función para enviar evento de personal_info_autofill
  const trackPersonalInfoAutofill = () => {
    try {
      sendGTMEvent({
        event: 'personal_info_autofill',
      });
    } catch (error) {
      console.error('Error tracking personal info autofill:', error);
    }
  };

  // Función para enviar evento de newsletter_subscription
  const trackNewsletterSubscription = () => {
    try {
      sendGTMEvent({
        event: 'newsletter_subscription',
      });
    } catch (error) {
      console.error('Error tracking newsletter subscription:', error);
    }
  };

  return {
    trackPageView,
    trackViewItem,
    trackViewItemList,
    trackSelectItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddPersonalInfo,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase,
    trackRefund,
    trackViewPromotion,
    trackSelectPromotion,
    trackCustomEvent,
    trackSignUp,
    trackLogin,
    trackPersonalInfoAutofill,
    trackNewsletterSubscription,
  };
};
